if (!customElements.get("component-cerros-locator")) {
  let embed = null;
  let suggestId = 0;

  const loadEmbed = (origin) => {
    embed ||= new Promise((resolve, reject) => {
      const tag = document.createElement("script");

      tag.src = `${origin}/locator/embed.js`;
      tag.async = true;
      tag.onload = () => (window.CerrosLocator ? resolve(window.CerrosLocator) : reject(new Error("cerros embed did not register")));
      tag.onerror = () => reject(new Error("cerros embed unavailable"));

      document.head.appendChild(tag);
    });

    return embed;
  };

  const titleCase = (text) => text
    .toLowerCase()
    .replace(/(^|[\s\-\/(])([a-z])/g, (match, lead, letter) => lead + letter.toUpperCase())
    .replace(/\b([A-Za-z]{2})(?=\s+\d{5}\b)/g, (state) => state.toUpperCase())
    .replace(/\b(\d+(?:\.\d+)?) (Mi|Km)\b/g, (match, value, unit) => `${value} ${unit.toLowerCase()}`);

  const distanceLabel = (miles) => {
    if (!Number.isFinite(miles)) return "";
    if (miles < 0.1) return `${Math.max(50, Math.round((miles * 5280) / 50) * 50)} ft`;
    if (miles < 10) return `${miles.toFixed(1)} mi`;

    return `${Math.round(miles)} mi`;
  };

  const milesBetween = (a, b) => {
    const radians = (value) => (value * Math.PI) / 180;
    const lat = radians(b.lat - a.lat);
    const lng = radians(b.lng - a.lng);
    const h = Math.sin(lat / 2) ** 2 + Math.cos(radians(a.lat)) * Math.cos(radians(b.lat)) * Math.sin(lng / 2) ** 2;

    return 2 * 3958.8 * Math.asin(Math.sqrt(h));
  };

  class CerrosLocator extends Theme.Component {
    instance = null;
    timer = null;
    tidier = null;
    tidyFrame = null;
    pendingZip = null;
    listeners = null;
    collapsing = false;
    searching = false;

    connectedCallback() {
      super.connectedCallback();

      this.listeners = new AbortController();
      this.addEventListener("click", (event) => this.handleCollapse(event), { capture: true, signal: this.listeners.signal });
      this.addEventListener("click", (event) => this.handleCardClick(event), { signal: this.listeners.signal });
      this.mobileQuery = window.matchMedia("(max-width: 1023px), (pointer: coarse)");
      this.mobileQuery.addEventListener("change", () => this.applyMapPadding(), { signal: this.listeners.signal });

      this.pendingZip = Theme.URLParams.get("zip");

      this.addDirectEvent("cerros:zip", document, (event) => {
        this.search(event.detail.zip);
      });

      this.intersectionObserver((entry, observer) => {
        if (!entry.isIntersecting) return;

        observer.disconnect();
        this.mount();
      }, { rootMargin: "400px" });
    }

    disconnectedCallback() {
      super.disconnectedCallback();

      this.listeners?.abort();
      clearTimeout(this.timer);
      cancelAnimationFrame(this.tidyFrame);
      this.tidier?.disconnect();
      this.heightObserver?.disconnect();
      this.instance?.destroy?.();
      this.instance = null;
    }

    get origin() {
      return (this.dataset.api || "").replace(/\/+$/, "");
    }

    get isProduct() {
      return this.dataset.context === "product";
    }

    get themeConfig() {
      const styles = getComputedStyle(document.documentElement);
      const token = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;

      const config = {
        colors: { primary: "#9E98C2", accent: token("--color-6", "#68C3F9") },
        borderRadius: 10,
        logoUrl: null,
        showHeading: false,
        units: "mi",
        doorProducts: {
          showCount: true,
          expandable: !this.isProduct,
          stockBadge: "days",
          showInStock: false,
          buyLinks: false,
          tapToFilter: false,
          showChipBadges: false
        }
      };

      if (this.dataset.title) {
        return { ...config, showHeading: true, title: this.dataset.title, subtitle: "" };
      }

      return config;
    }

    async mount() {
      if (!this.origin) return;

      let api = null;

      try {
        api = await loadEmbed(this.origin);
      } catch (error) {
        this.fail();
        return;
      }

      if (!this.isConnected) return;

      this.addDirectEvent("cerros-locator:ready", this, () => this.ready());
      this.addDirectEvent("cerros-buy:product", this, () => this.ready());

      this.timer = setTimeout(() => this.fail(), 10000);

      const options = { base: this.origin, configOverride: this.themeConfig };

      this.tidier = new MutationObserver(() => {
        cancelAnimationFrame(this.tidyFrame);
        this.tidyFrame = requestAnimationFrame(() => this.tidy());
      });
      this.tidier.observe(this.elements.mount, { childList: true, subtree: true });

      this.instance = this.isProduct ? this.mountProduct(api, options) : api.init(this.elements.mount, options);
    }

    mountProduct(api, options) {
      const instance = api.initBuy(this.elements.mount, { ...options, product: this.dataset.product });
      const client = instance?.api;

      if (client?.product) {
        const fetchProduct = client.product.bind(client);

        client.product = async (...args) => {
          const data = await fetchProduct(...args);

          return { ...data, widget: { ...data.widget, defaultTab: "online", askZip: true } };
        };
      }

      return instance;
    }

    get flavors() {
      try {
        return this.data.flavors || {};
      } catch (error) {
        return {};
      }
    }

    storeProducts(store) {
      const catalog = this.instance?.catalog || {};
      const seen = new Set();

      return (store.products || []).reduce((list, entry) => {
        const product = this.resolveProduct(catalog[entry.id]);

        if (!product || seen.has(product.key)) return list;

        seen.add(product.key);
        list.push(product);

        return list;
      }, []);
    }

    resolveProduct(item) {
      if (!item) return null;

      const [lineId = "", slug = ""] = (item.filterId || "").split(":");
      const title = item.title || "";

      const match = Object.entries(this.flavors)
        .filter(([handle]) => slug && handle.startsWith(`${slug}-`))
        .sort(([, a], [, b]) => b.prefix.length - a.prefix.length)
        .find(([, flavor]) => title.startsWith(`${flavor.prefix} `));

      if (match) {
        const [handle, flavor] = match;

        return { key: handle, name: flavor.name, line: flavor.line, image: flavor.image || item.imageUrl, color: flavor.color };
      }

      const filter = this.instance?.cfg?.filters?.find((entry) => entry.id === lineId);

      return {
        key: item.filterId || title,
        name: slug ? slug.replace(/-/g, " ") : title.split(" — ")[0],
        line: (filter?.label || "").replace(/^Recess\s+/, ""),
        image: item.imageUrl,
        color: ""
      };
    }

    tidyStoreCount() {
      const controls = this.querySelector(".cl-controls");

      if (!this.isProduct || !controls) return;

      const total = this.querySelectorAll(".cl-list .cl-item").length;
      let label = controls.querySelector(".cerros-store-count");

      if (!total) {
        label?.remove();
        return;
      }

      if (!label) {
        label = document.createElement("span");
        label.className = "cerros-store-count";
        controls.append(label);
      }

      const text = `${total} ${total === 1 ? "store" : "stores"}`;

      if (label.textContent !== text) label.textContent = text;
    }

    tidyCounts() {
      this.querySelectorAll(".cl-toggle[data-products]").forEach((toggle) => {
        const store = this.instance?.stores?.find((entry) => entry.id === Number(toggle.dataset.products));
        const label = Array.from(toggle.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);

        if (!store || !label) return;

        const count = this.storeProducts(store).length;
        const text = `${count} ${count === 1 ? "product" : "products"}`;

        if (label.nodeValue !== text) label.nodeValue = text;
      });
    }

    tidyGrids() {
      const catalog = this.instance?.catalog || {};

      this.querySelectorAll(".cl-grid:not([data-tidied])").forEach((grid) => {
        const id = grid.dataset.store || grid.id.replace("cl-grid-", "");
        const store = this.instance?.stores?.find((entry) => String(entry.id) === id);

        grid.setAttribute("data-tidied", "");

        if (!store) return;

        const seen = new Set();

        Array.from(grid.querySelectorAll(".cl-tile")).forEach((tile, index) => {
          const product = this.resolveProduct(catalog[store.products?.[index]?.id]);

          if (!product) return;

          if (seen.has(product.key)) {
            tile.remove();
            return;
          }

          seen.add(product.key);
          this.decorateTile(tile, product, seen.size - 1);
        });
      });
    }

    popupStore() {
      const stores = this.instance?.stores || [];
      const selected = stores.find((entry) => entry.id === this.instance.selectedId);

      if (selected) return selected;

      const point = this.instance?.map?.popup?.getLngLat?.();

      if (!point) return null;

      return stores.find((entry) => Math.abs(entry.lat - point.lat) < 1e-6 && Math.abs(entry.lng - point.lng) < 1e-6) || null;
    }

    tidyPopup() {
      const count = this.querySelector(".cl-popup-count:not([data-tidied])");

      if (!count) return;

      count.setAttribute("data-tidied", "");

      const store = this.popupStore();
      const actions = count.closest(".maplibregl-popup-content")?.querySelector(".cl-popup-actions");

      if (!store?.products?.length || !actions || typeof this.instance.productGrid !== "function") return;

      const total = this.storeProducts(store).length;
      const button = document.createElement("button");

      button.type = "button";
      button.className = "cl-action cl-count cl-toggle";
      button.setAttribute("aria-expanded", "false");
      button.textContent = `${total} ${total === 1 ? "product" : "products"}`;
      button.addEventListener("click", () => this.togglePopupProducts(button, store));

      actions.prepend(button);
      count.remove();
    }

    togglePopupProducts(button, store) {
      const content = button.closest(".maplibregl-popup-content");

      if (!content) return;

      const open = content.querySelector(".cerros-popup-products");

      if (open) {
        this.closePopupProducts(open, button);
        return;
      }

      const holder = document.createElement("template");

      holder.innerHTML = this.instance.productGrid(store);

      const grid = holder.content.firstElementChild;

      if (!grid) return;

      const panel = document.createElement("div");

      panel.className = "cerros-popup-products";
      grid.removeAttribute("id");
      grid.dataset.store = store.id;
      panel.append(grid);
      content.append(panel);

      this.tidyGrids();
      button.setAttribute("aria-expanded", "true");
      this.placePopupProducts(panel);
    }

    closePopupProducts(panel, button) {
      button?.setAttribute("aria-expanded", "false");

      if (this.calm) {
        panel.remove();
        return;
      }

      panel.animate(
        [{ opacity: 1, transform: "none" }, { opacity: 0, transform: "translateY(-6px)" }],
        { duration: 180, easing: "ease-in", fill: "forwards" }
      ).finished.then(() => panel.remove(), () => panel.remove());
    }

    placePopupProducts(panel) {
      const frame = this.querySelector(".cl-map")?.getBoundingClientRect();
      const popup = panel.parentElement?.getBoundingClientRect();
      const rect = panel.getBoundingClientRect();

      if (!frame || !popup) return;

      const roomBelow = frame.bottom - popup.bottom;
      const roomAbove = popup.top - frame.top;

      if (rect.bottom > frame.bottom - 8 && roomAbove > roomBelow) panel.classList.add("state--above");
    }

    get calm() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    get mobile() {
      return !!this.mobileQuery?.matches;
    }

    applyMapPadding() {
      const map = this.instance?.map?.map;

      if (typeof map?.setPadding !== "function") return;

      if (this.isProduct) {
        map.setPadding({ top: 0, right: 0, bottom: 150, left: 0 });
        return;
      }

      map.setPadding({ top: this.mobile ? 90 : 0, right: 0, bottom: 0, left: 0 });
    }

    get isOverlay() {
      return this.dataset.context === "overlay";
    }

    handleCardClick(event) {
      if (!this.mobile || !event.target.closest(".cl-list .cl-item-main")) return;

      this.querySelector(".cl-map")?.scrollIntoView({ behavior: this.calm ? "auto" : "smooth", block: "center" });
    }

    keepInPlace(toggle) {
      const id = toggle.dataset.products;
      const before = toggle.getBoundingClientRect().top;

      requestAnimationFrame(() => requestAnimationFrame(() => {
        const next = this.querySelector(`.cl-list .cl-toggle[data-products="${CSS.escape(id)}"]`);

        if (!next) return;

        const delta = next.getBoundingClientRect().top - before;
        const list = this.querySelector(".cl-list");

        if (Math.abs(delta) < 1) return;

        if (list && getComputedStyle(list).overflowY !== "visible" && list.scrollHeight > list.clientHeight + 1) {
          list.scrollTop += delta;
        } else {
          window.scrollBy(0, delta);
        }
      }));
    }

    handleCollapse(event) {
      if (this.collapsing) return;

      const toggle = event.target.closest(".cl-list .cl-toggle");

      if (!toggle) return;

      if (toggle.getAttribute("aria-expanded") !== "true") {
        this.keepInPlace(toggle);
        return;
      }

      const grid = toggle.closest(".cl-item")?.querySelector(".cl-grid");

      if (!grid) return;

      event.preventDefault();
      event.stopPropagation();
      toggle.setAttribute("aria-expanded", "false");

      this.collapseGrid(grid).then(() => {
        if (!toggle.isConnected) return;

        this.keepInPlace(toggle);
        this.collapsing = true;
        toggle.click();
        this.collapsing = false;
      });
    }

    collapseGrid(grid) {
      if (this.calm) return Promise.resolve();

      const styles = getComputedStyle(grid);

      return grid.animate(
        [
          { maxHeight: `${grid.offsetHeight}px`, paddingTop: styles.paddingTop, paddingBottom: styles.paddingBottom, marginTop: styles.marginTop, marginBottom: styles.marginBottom, opacity: 1 },
          { maxHeight: "0px", paddingTop: "0px", paddingBottom: "0px", marginTop: "0px", marginBottom: "0px", opacity: 0 }
        ],
        { duration: 260, easing: "cubic-bezier(0.4, 0, 0.2, 1)", fill: "forwards" }
      ).finished.catch(() => {});
    }

    decorateTile(tile, product, index) {
      const image = tile.querySelector(".cl-tile-img img");
      const title = tile.querySelector(".cl-tile-title");
      const line = document.createElement("span");
      const name = document.createElement("span");

      if (image && product.image) image.src = product.image;

      line.className = "cerros-tile-line";
      line.textContent = product.line;
      name.className = "cerros-tile-name";
      name.textContent = product.name;

      title?.replaceChildren(line, name);
      tile.querySelectorAll(".cl-tile-badge").forEach((badge) => badge.remove());
      tile.style.setProperty("--index", index);
    }

    storeDistance(store) {
      const origin = this.instance?.search;

      if (!store || !origin || !Number.isFinite(origin.lat) || !Number.isFinite(store.lat)) return "";

      return distanceLabel(milesBetween(origin, store));
    }

    distanceTag(text) {
      const tag = document.createElement("span");

      tag.className = "cerros-distance";
      tag.textContent = text;

      return tag;
    }

    relabelDirections(scope) {
      scope.querySelectorAll("a.cl-action[href*='google.com/maps'], a.cl-action[href*='maps.apple.com']").forEach((link) => {
        const label = Array.from(link.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.nodeValue.trim());

        if (label && label.nodeValue.trim() === "Directions") label.nodeValue = "Get Directions";
      });
    }

    tidyCards() {
      this.querySelectorAll(".cl-list .cl-item:not([data-card])").forEach((item) => {
        const store = this.instance?.stores?.find((entry) => String(entry.id) === item.dataset.id);
        const label = item.querySelector(".cl-item-dist");
        const distance = this.storeDistance(store);

        const logo = item.querySelector(".cl-item-logo");
        const address = item.querySelector(".cl-item-addr");

        this.hideBrokenLogo(logo);

        const parts = (address?.textContent || "").split(" · ");

        item.setAttribute("data-card", "");

        if (label && distance) label.textContent = distance;

        if (this.isOverlay && parts.length > 1) address.textContent = parts.slice(0, 2).join("\n");
      });
    }

    hideBrokenLogo(logo) {
      const image = logo?.querySelector("img");

      if (!image) return;

      const drop = () => logo.remove();

      if (image.complete && !image.naturalWidth) {
        drop();
        return;
      }

      image.addEventListener("error", drop, { once: true });
    }

    tidyPopupLayout() {
      const body = this.querySelector(".cl-popup-body:not([data-layout])");

      if (!body) return;

      body.setAttribute("data-layout", "");

      const store = this.popupStore();
      const address = body.querySelector("h4 + p");
      const actions = body.parentElement?.querySelector(".cl-popup-actions");
      const parts = (address?.textContent || "").split(" · ");
      const distance = this.storeDistance(store) || (parts.length > 2 ? parts[parts.length - 1] : "");

      if (address && parts.length > 1) address.textContent = parts.slice(0, 2).join("\n");

      if (distance) body.querySelector("h4")?.after(this.distanceTag(distance));

      if (actions) this.relabelDirections(actions);
    }

    tidyPopupPhone() {
      const actions = this.querySelector(".cl-popup-actions:not([data-phone])");

      if (!actions) return;

      actions.setAttribute("data-phone", "");
      this.instance?.map?.popup?.setMaxWidth?.("none");

      const native = actions.parentElement?.querySelector(".cl-popup-phone");
      const source = native?.querySelector("a[href^='tel:']");
      const phone = source?.textContent.trim() || this.popupStore()?.phone;

      native?.remove();

      if (!phone || actions.querySelector("a[href^='tel:']")) return;

      const link = document.createElement("a");

      link.className = "cl-action";
      link.href = source?.getAttribute("href") || `tel:${phone.replace(/[^\d+]/g, "")}`;
      link.textContent = phone;
      actions.append(link);
    }

    tidyPhones() {
      this.querySelectorAll("a.cl-action[href^='tel:']:not([data-tidied])").forEach((link) => {
        const digits = link.getAttribute("href").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
        const number = digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : digits;
        const label = Array.from(link.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);

        link.setAttribute("data-tidied", "");
        link.setAttribute("aria-label", `Call ${number}`);

        if (!label || !number) return;

        const full = document.createElement("span");
        const short = document.createElement("span");

        full.className = "cerros-phone-number";
        full.textContent = number;
        short.className = "cerros-phone-call";
        short.textContent = "Call";
        label.replaceWith(full, short);
      });
    }

    tidyList() {
      const list = this.querySelector(".cl-list");

      if (!list || (!this.isProduct && !this.mobile)) return;

      const items = Array.from(list.querySelectorAll(":scope > .cl-item"));
      const key = `${items.length}:${items[0]?.dataset.id || ""}`;

      if (key !== this.listKey) {
        this.listKey = key;
        this.visibleStores = 5;
      }

      items.forEach((item, index) => item.classList.toggle("state--overflow", index >= this.visibleStores));

      let row = this.querySelector(".cerros-more-row");

      if (!row) {
        row = document.createElement("div");
        row.className = "cerros-more-row";
        row.append(
          this.listButton("less", "Show Fewer", () => this.showFewerStores()),
          this.listButton("more", "Show More Stores", () => {
            this.visibleStores += 5;
            this.tidyList();
          })
        );
        list.after(row);
      }

      const more = row.querySelector("[data-list='more']");
      const less = row.querySelector("[data-list='less']");

      more.hidden = items.length <= this.visibleStores;
      less.hidden = this.visibleStores <= 5;
      row.hidden = more.hidden && less.hidden;
    }

    listButton(action, label, handler) {
      const button = document.createElement("button");

      button.type = "button";
      button.className = "cerros-more button button-2 button--small";
      button.dataset.list = action;
      button.textContent = label;
      button.addEventListener("click", handler);

      return button;
    }

    showFewerStores() {
      const list = this.querySelector(".cl-list");

      this.visibleStores = 5;
      this.tidyList();

      if (list && list.getBoundingClientRect().top < 0) list.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    tidyImages() {
      this.querySelectorAll("img[src*='/_next/image']").forEach((image) => {
        const source = new URL(image.src, location.href).searchParams.get("url");

        if (source) image.src = source;
      });
    }

    tidyCombobox() {
      const input = this.querySelector(".cl-input[aria-autocomplete][aria-expanded]");
      const list = this.querySelector(".cl-suggest");

      if (!input || !list) return;

      if (!list.id) list.id = `cerros-suggest-${(suggestId += 1)}`;

      if (input.getAttribute("role") !== "combobox") input.setAttribute("role", "combobox");

      if (input.getAttribute("aria-controls") !== list.id) input.setAttribute("aria-controls", list.id);
    }

    tidyOfferMarks() {
      this.querySelectorAll(".cb-offer").forEach((offer) => {
        const mark = offer.querySelector(".cb-offer-mark");

        if (!mark) return;

        const label = offer.querySelector(".cb-offer-label")?.textContent.trim();
        const override = this.marks[label];
        const logo = override || this.retailers[label];
        const current = mark.querySelector("img");

        if (!logo || (current && (!override || current.dataset.mark === label))) return;

        const img = document.createElement("img");

        img.src = logo;
        img.alt = "";
        img.loading = "lazy";
        img.dataset.mark = label;
        mark.replaceChildren(img);
      });
    }

    tidy() {
      this.tidyIntro();
      this.tidyTabs();
      this.tidyStoreCount();
      this.tidyImages();
      this.tidyCombobox();
      this.tidyOfferMarks();

      if (this.classList.contains("state--ready")) this.addFindButton();
      this.tidyCounts();
      this.tidyList();
      this.tidyCards();
      this.tidyPopupLayout();
      this.tidyPopupPhone();
      this.tidyPhones();
      this.tidyFilters();
      this.tidyPopup();
      this.tidyGrids();

      const submit = this.querySelector(".cb-ziprow [type='submit']");

      if (submit && submit.textContent.trim() !== "find") {
        submit.textContent = "find";
      }

      this.querySelectorAll(".cl-item-addr, .cl-popup-body p").forEach((element) => {
        if (element.children.length) return;

        const text = titleCase(element.textContent);

        if (text !== element.textContent) element.textContent = text;
      });
    }

    ready() {
      clearTimeout(this.timer);

      if (this.classList.contains("state--ready")) return;

      this.classList.add("state--ready");

      this.addFindButton();

      this.applyMapPadding();

      if (this.isProduct) this.matchPanelHeights();

      if (this.isProduct) this.smoothTabs();

      if (!this.isProduct) this.addFilter();

      if (!this.isProduct) this.trackSearch();

      if (this.pendingZip) this.search(this.pendingZip);

      if (!this.isProduct && !this.pendingZip) this.autoLocate();
    }

    async autoLocate() {
      let state = "";

      try {
        state = (await navigator.permissions.query({ name: "geolocation" })).state;
      } catch (error) {
        return;
      }

      if (state !== "granted" || !this.isConnected || this.searching) return;

      await new Promise((resolve) => requestAnimationFrame(resolve));

      this.querySelector(".cl-locate")?.click();
    }

    fail() {
      if (this.classList.contains("state--ready")) return;

      this.classList.add("state--failed");
    }

    addFindButton() {
      const field = this.querySelector(".cl-searchrow .cl-field");

      if (!field || this.querySelector(".cerros-locator-find")) return;

      const button = document.createElement("button");

      button.type = "button";
      button.className = "cerros-locator-find button button-1 button--small";
      button.textContent = "find";
      button.addEventListener("click", () => {
        this.dispatchEvent(new CustomEvent("locator:find", {
          bubbles: true,
          detail: { tab: this.activeTab }
        }));

        this.submit();
      });

      field.after(button);
    }

    get lines() {
      try {
        return this.data.lines || {};
      } catch (error) {
        return {};
      }
    }

    get retailers() {
      try {
        return this.data.retailers || {};
      } catch (error) {
        return {};
      }
    }

    get marks() {
      try {
        return this.data.marks || {};
      } catch (error) {
        return {};
      }
    }

    addFilter() {
      const row = this.querySelector(".cl-search .cl-searchrow");
      const chips = this.querySelector(".cl-chips-wrap");

      if (!row || !chips || this.querySelector(".cerros-filter")) return;

      const filter = document.createElement("div");
      const toggle = document.createElement("button");
      const value = document.createElement("span");
      const clear = document.createElement("button");
      const menu = document.createElement("div");
      const heading = document.createElement("p");
      const back = document.createElement("button");
      const footer = document.createElement("div");
      const apply = document.createElement("button");

      filter.className = "cerros-filter";

      toggle.type = "button";
      toggle.className = "cerros-filter-toggle button button-2 button--small";
      toggle.setAttribute("aria-expanded", "false");
      value.className = "cerros-filter-value";
      value.textContent = "Select Specific Products";
      toggle.append(value);

      clear.type = "button";
      clear.className = "cerros-filter-clear";
      clear.setAttribute("aria-label", "Clear product filter");
      clear.hidden = true;

      menu.className = "cerros-filter-menu";
      menu.hidden = true;

      heading.className = "cerros-filter-heading";
      heading.textContent = "Select a Category";

      back.type = "button";
      back.className = "cerros-filter-back";
      back.textContent = "All Categories";
      back.addEventListener("click", () => this.showCategories());

      footer.className = "cerros-filter-footer";
      apply.type = "button";
      apply.className = "button button-1 button--small";
      apply.textContent = "Show Stores";
      apply.addEventListener("click", () => this.toggleFilter(false));
      footer.append(apply);

      toggle.addEventListener("click", () => this.toggleFilter());
      clear.addEventListener("click", () => this.resetFilter());
      menu.addEventListener("click", (event) => this.handleFilterClick(event), { capture: true });

      filter.addEventListener("keydown", (event) => {
        if (event.key !== "Escape" || menu.hidden) return;

        this.toggleFilter(false);
        toggle.focus();
      });

      document.addEventListener("click", (event) => {
        if (!menu.hidden && !event.composedPath().includes(filter)) this.toggleFilter(false);
      }, { signal: this.listeners.signal });

      menu.append(back, heading, chips, footer);
      filter.append(toggle, clear, menu);
      row.after(filter);

      this.relabelFilters();
    }

    matchPanelHeights() {
      const online = this.querySelector(".cb-online");

      if (!online || typeof ResizeObserver !== "function") return;

      this.heightObserver = new ResizeObserver(() => {
        const height = online.getBoundingClientRect().height;

        if (height) this.style.setProperty("--cerros-online-height", `${Math.round(height)}px`);
      });

      this.heightObserver.observe(online);
    }

    smoothTabs() {
      this.addEventListener(
        "click",
        (event) => {
          const tab = event.target.closest(".cb-tab");
          const root = this.querySelector(".cb-root");

          if (!tab || !root || tab.classList.contains("cb-on") || this.calm) return;

          const from = root.getBoundingClientRect().height;

          requestAnimationFrame(() => {
            const to = root.getBoundingClientRect().height;
            const panel = root.querySelector(".cb-panel:not([hidden])");

            panel?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 250, easing: "ease" });

            if (Math.abs(to - from) < 2) return;

            root.animate([{ height: `${from}px` }, { height: `${to}px` }], {
              duration: 350,
              easing: "cubic-bezier(0.2, 0.8, 0.2, 1)"
            });
          });
        },
        { capture: true, signal: this.listeners.signal }
      );
    }

    tidyTabs() {
      const tab = this.querySelector(".cb-tab[data-tab='in-store']");

      if (tab && tab.textContent !== "In Store") tab.textContent = "In Store";
    }

    tidyIntro() {
      const intro = Array.from(this.querySelectorAll(".cl-empty h3")).some((heading) => heading.textContent.trim() === "Where are you?");

      this.classList.toggle("state--intro", intro);
    }

    trackSearch() {
      const instance = this.instance;

      if (typeof instance?.runSearch !== "function") return;

      const runSearch = instance.runSearch.bind(instance);

      instance.runSearch = (...args) => {
        this.searching = true;
        this.tidyFilters();

        return Promise.resolve(runSearch(...args)).finally(() => {
          this.searching = false;
          this.tidyFilters();

          if (instance.search === args[0]) this.fitResults();
        });
      };
    }

    fitResults() {
      const map = this.instance?.map?.map;
      const origin = this.instance?.search;
      const stores = (this.instance?.stores || []).slice(0, 8);

      if (typeof map?.fitBounds !== "function" || !origin || !stores.length) return;

      const points = [origin, ...stores];
      const lats = points.map((point) => point.lat);
      const lngs = points.map((point) => point.lng);

      map.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: this.resultsPadding, maxZoom: 13, duration: this.calm ? 0 : 600 }
      );
    }

    get resultsPadding() {
      if (this.mobile || !this.isOverlay) return 40;

      return { top: 100, right: 60, bottom: 60, left: 410 };
    }

    toggleFilter(force) {
      const filter = this.querySelector(".cerros-filter");
      const menu = filter?.querySelector(".cerros-filter-menu");
      const toggle = filter?.querySelector(".cerros-filter-toggle");

      if (!menu || !toggle) return;

      const open = force ?? menu.hidden;

      menu.hidden = !open;
      toggle.setAttribute("aria-expanded", String(open));
      filter.classList.toggle("state--open", open);
      this.tidyFilters();
    }

    handleFilterClick(event) {
      const instance = this.instance;

      if (event.target.closest(".cl-flavor[data-flavor='']")) {
        setTimeout(() => this.toggleFilter(false));
        return;
      }

      const line = event.target.closest(".cl-chip-main[data-filter]");

      if (!instance?.selectedFilters || !line || line.disabled) return;

      event.preventDefault();
      event.stopPropagation();

      const id = line.dataset.filter;
      const entry = instance.cfg?.filters?.find((item) => item.id === id);
      const selected = !!entry && (instance.selectedFilters.has(id) || (entry.flavors || []).some((flavor) => instance.selectedFilters.has(flavor.id)));

      instance.openFlavorChip = id;

      if (selected) {
        instance.renderChips();
        return;
      }

      instance.selectedFilters.clear();
      instance.selectedFilters.add(id);
      instance.applyFilters();
    }

    showCategories() {
      const instance = this.instance;

      if (!instance || typeof instance.renderChips !== "function") return;

      instance.openFlavorChip = null;
      instance.renderChips();
    }

    resetFilter() {
      const instance = this.instance;

      if (!instance?.selectedFilters) return;

      instance.selectedFilters.clear();
      instance.openFlavorChip = null;
      instance.applyFilters();
    }

    relabelFilters() {
      const filters = this.instance?.cfg?.filters;

      if (!filters?.length || typeof this.instance.renderChips !== "function") return;

      const lines = Object.entries(this.lines);

      this.lineLabels = {};

      filters.forEach((filter) => {
        const short = filter.label.replace(/^Recess\s+(?=\S)/, "");
        const match = lines.find(([name]) => name === short) || lines.find(([name]) => name.includes(short.replace(/s$/, "")));

        this.lineLabels[filter.id] = filter.label;
        filter.imageUrl = null;
        filter.label = match ? match[0] : short;
        filter.description = match ? match[1].description || "" : "";
      });

      this.instance.renderChips();
    }

    lineNames(lineId) {
      const current = this.instance?.cfg?.filters?.find((entry) => entry.id === lineId)?.label;

      return [this.lineLabels?.[lineId], current].filter(Boolean);
    }

    flavorRecord(id) {
      const [lineId = "", slug = ""] = id.split(":");
      const names = this.lineNames(lineId);

      const match = Object.entries(this.flavors).find(([handle, flavor]) => {
        if (!handle.startsWith(`${slug}-`)) return false;

        return names.some((name) => name === flavor.prefix || name === flavor.line);
      });

      return match?.[1] || null;
    }

    leadFlavor(entry) {
      return (entry?.flavors || [])
        .filter((flavor) => !flavor.id.endsWith(":variety"))
        .sort((a, b) => (b.count || 0) - (a.count || 0))
        .find((flavor) => this.flavorRecord(flavor.id)?.image);
    }

    leadArt(entry) {
      const line = this.lineNames(entry?.id).map((name) => this.lines[name]).find((record) => record?.featured);

      if (line) return { image: line.featured, color: line.featuredColor || "" };

      const lead = this.leadFlavor(entry);

      if (!lead) return null;

      return { image: this.flavorRecord(lead.id).image, color: this.flavorColor(lead.id) };
    }

    flavorColor(id) {
      return this.flavorRecord(id)?.color || "";
    }

    flavorImage(id) {
      const [lineId = ""] = id.split(":");
      const record = this.flavorRecord(id);

      if (record?.image) return record.image;

      const sampler = this.lineNames(lineId).map((name) => this.lines[name]?.sampler).find(Boolean);

      if (sampler) return sampler;

      const item = Object.values(this.instance?.catalog || {}).find((entry) => entry.filterId === id && entry.imageUrl);

      return item?.imageUrl || "";
    }

    tidyFilters() {
      const filter = this.querySelector(".cerros-filter");

      if (!filter) return;

      const instance = this.instance;
      const focused = instance?.openFlavorChip || "";
      const filters = instance?.cfg?.filters || [];
      const selected = instance?.selectedFilters || new Set();

      filter.classList.toggle("state--focused", !!focused);

      const apply = filter.querySelector(".cerros-filter-footer .button");
      const count = this.querySelectorAll(".cl-list .cl-item").length;
      const label = this.searching ? "Finding Stores" : count ? `Show ${count} ${count === 1 ? "Store" : "Stores"}` : "Show Stores";

      apply?.classList.toggle("state--loading", !!this.searching);

      if (apply && apply.textContent !== label) apply.textContent = label;

      filter.querySelectorAll(".cl-chip-main[data-filter]").forEach((main) => {
        const entry = filters.find((item) => item.id === main.dataset.filter);
        const name = main.querySelector(".cl-chip-text");

        main.removeAttribute("title");

        if (entry && name && name.textContent !== entry.label) name.textContent = entry.label;

        if (entry && !main.querySelector(".cerros-chip-media")) {
          const art = this.leadArt(entry);

          if (art?.image) {
            const media = document.createElement("span");
            const img = document.createElement("img");

            media.className = "cerros-chip-media";
            img.src = art.image;
            img.alt = "";
            img.loading = "lazy";
            media.append(img);
            main.prepend(media);

            if (art.color) main.style.setProperty("--cerros-chip-color", art.color);
          }
        }

        if (!entry?.description || main.querySelector(".cerros-chip-description")) return;

        const description = document.createElement("span");

        description.className = "cerros-chip-description";
        description.textContent = entry.description;
        main.append(description);
      });

      filter.querySelectorAll(".cl-flavor[data-flavor]:not([data-flavor=''])").forEach((button) => {
        if (button.querySelector(".cerros-flavor-media")) return;

        const image = this.flavorImage(button.dataset.flavor);

        if (!image) return;

        const media = document.createElement("span");
        const img = document.createElement("img");

        media.className = "cerros-flavor-media";
        img.src = image;
        img.alt = "";
        img.loading = "lazy";
        media.append(img);
        button.prepend(media);
      });

      filter.querySelectorAll(".cl-flavor[data-flavor]:not([data-flavor=''])").forEach((button) => {
        if (button.querySelector(".cerros-flavor-label")) return;

        const node = [...button.childNodes].find((child) => child.nodeType === Node.TEXT_NODE && child.textContent.trim());

        if (!node) return;

        const label = document.createElement("span");
        const text = node.textContent.trim();
        const split = text.lastIndexOf(" ");

        label.className = "cerros-flavor-label";
        label.textContent = split === -1 ? text : `${text.slice(0, split)}\n${text.slice(split + 1)}`;
        node.replaceWith(label);
      });

      const labels = filters.flatMap((entry) => {
        if (selected.has(entry.id)) return [entry.label];

        return (entry.flavors || []).filter((flavor) => selected.has(flavor.id)).map((flavor) => flavor.label);
      });
      const summary = labels.length ? `${labels[0]}${labels.length > 1 ? ` +${labels.length - 1}` : ""}` : "";
      const text = summary ? `Products: ${summary}` : "Select Specific Products";
      const value = filter.querySelector(".cerros-filter-value");
      const heading = filter.querySelector(".cerros-filter-heading");
      const title = focused ? "Select a Specific Product" : "Select a Category";

      if (value && value.textContent !== text) value.textContent = text;
      if (heading && heading.textContent !== title) heading.textContent = title;

      filter.querySelector(".cerros-filter-toggle")?.classList.toggle("state--active", !!summary);
      filter.querySelector(".cerros-filter-clear").hidden = !summary;
    }

    submit() {
      const input = this.querySelector(".cl-search .cl-input");

      if (!input || !input.value.trim()) {
        input?.focus();
        return;
      }

      this.dispatchEvent(new CustomEvent("locator:search", {
        bubbles: true,
        detail: { tab: this.activeTab }
      }));

      input.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", bubbles: true, cancelable: true }));
    }

    get activeTab() {
      const tab = this.querySelector(".cb-tab[aria-selected='true'], .cb-tab.cb-on");

      if (!tab) {
        return undefined;
      }

      return tab.dataset.tab === "in-store" ? "in_store" : "online";
    }

    search(zip) {
      if (!zip) return;

      if (!this.classList.contains("state--ready") || this.isProduct) {
        this.pendingZip = zip;
        return;
      }

      this.pendingZip = null;

      const input = this.querySelector(".cl-search .cl-input");

      if (!input) return;

      input.value = zip;
      this.submit();
    }
  }

  customElements.define("component-cerros-locator", CerrosLocator);
}
